import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { BOOKING_QUERY_KEY } from 'app/data/queries/use-booking-query';
import { ACTIVITY_FEED_QUERY_KEY } from 'app/data/queries/use-activity-feed-query';

/** helix `CheckInStatus.CONFIRMED` — the only status a user can put a check-in into. */
const CONFIRMED_STATUS = 'confirmed';

interface CheckInBody {
  status: string;
}

/**
 * Check in to a resource booking. `PATCH /api/check-in/{transactionUuid}`.
 *
 * Keyed on the transaction UUID, unlike cancelling, which wants the numeric id — so this needs only
 * what the feed item already carries. The same endpoint tenant-portal's booking card calls.
 */
export const useCheckInBookingMutation = (): UseMutationResult<void, Error, string> => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (transactionUuid: string) => {
      const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
      await apiRequest<void, CheckInBody, void>({
        url: `${apiUrl}/api/check-in/${encodeURIComponent(transactionUuid)}`,
        method: HttpMethod.PATCH,
        headers,
        body: { status: CONFIRMED_STATUS },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_FEED_QUERY_KEY] });
    },
  });
};
